import { API } from '@pibex/shared';
import type { Invitation, InvitationResponse, InvitationStatus } from '@pibex/shared';
import { useId, useState } from 'react';
import { Badge } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { Notice } from '../../components/Notice.js';
import { es } from '../../i18n/es.js';
import { api, isApiError } from '../../lib/api.js';
import { formatDateTime } from '../../lib/trip.js';

const t = es.adminPage.invitations;

const statusTone: Record<InvitationStatus, 'warning' | 'success' | 'neutral'> = {
  pending: 'warning',
  accepted: 'success',
  revoked: 'neutral',
};

type Feedback = { tone: 'success' | 'error'; text: string } | null;

interface InvitationsListProps {
  invitations: Invitation[];
  onChanged(): Promise<void> | void;
}

export function InvitationsList({ invitations, onChanged }: InvitationsListProps) {
  const id = useId();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const run = async (invitationId: string, action: () => Promise<string>) => {
    setBusyId(invitationId);
    setFeedback(null);
    try {
      const text = await action();
      setFeedback({ tone: 'success', text });
      await onChanged();
    } catch (err) {
      if (isApiError(err, 'conflict')) setFeedback({ tone: 'error', text: t.conflict });
      else if (isApiError(err)) setFeedback({ tone: 'error', text: es.genericError });
      else setFeedback({ tone: 'error', text: es.networkError });
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  const resend = (inv: Invitation) =>
    run(inv.id, async () => {
      const { invitation } = await api.post<InvitationResponse>(API.adminInvitationResend(inv.id));
      return t.resent(invitation.email);
    });

  const revoke = (inv: Invitation) =>
    run(inv.id, async () => {
      await api.delete(API.adminInvitation(inv.id));
      return t.revoked(inv.email);
    });

  return (
    <section aria-labelledby={`${id}-heading`} className="flex flex-col gap-3">
      <h2 id={`${id}-heading`} className="text-lg font-semibold tracking-tight">
        {t.heading}
      </h2>
      <div aria-live="polite">
        {feedback ? <Notice tone={feedback.tone}>{feedback.text}</Notice> : null}
      </div>
      {invitations.length === 0 ? (
        <p className="rounded-md border border-dashed border-neutral-300 px-4 py-6 text-center text-sm text-neutral-500 dark:border-neutral-700 dark:text-neutral-400">
          {t.empty}
        </p>
      ) : (
        <ul role="list" className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {invitations.map((inv) => (
            <li key={inv.id} className="flex flex-col gap-3 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium break-all">{inv.email}</p>
                  <InvitationMeta invitation={inv} />
                </div>
                <Badge tone={statusTone[inv.status]}>{t.status[inv.status]}</Badge>
              </div>
              {inv.status === 'pending' ? (
                confirmId === inv.id ? (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    role="group"
                    aria-label={t.revokeConfirm(inv.email)}
                  >
                    <span className="text-sm">{t.revokeConfirm(inv.email)}</span>
                    <Button
                      variant="danger"
                      disabled={busyId === inv.id}
                      onClick={() => void revoke(inv)}
                    >
                      {es.confirm}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={busyId === inv.id}
                      onClick={() => setConfirmId(null)}
                    >
                      {es.cancel}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      disabled={busyId === inv.id}
                      onClick={() => void resend(inv)}
                    >
                      {busyId === inv.id ? t.resending : t.resend}
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busyId === inv.id}
                      onClick={() => setConfirmId(inv.id)}
                    >
                      {t.revoke}
                    </Button>
                  </div>
                )
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function InvitationMeta({ invitation }: { invitation: Invitation }) {
  const cls = 'mt-0.5 text-sm text-neutral-500 dark:text-neutral-400';
  if (invitation.status === 'accepted' && invitation.acceptedAt) {
    return <p className={cls}>{t.acceptedAt(formatDateTime(invitation.acceptedAt))}</p>;
  }
  if (invitation.emailError) {
    return (
      <p className="mt-0.5 text-sm text-red-700 dark:text-red-300">
        {t.sendError(invitation.emailError)}
      </p>
    );
  }
  if (invitation.emailSentAt) {
    return <p className={cls}>{t.sentAt(formatDateTime(invitation.emailSentAt))}</p>;
  }
  return <p className={cls}>{t.notSent}</p>;
}
